import { ActionIcon, Menu, Text, Tooltip } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { IconDotsVertical, IconPlus } from '@tabler/icons-react';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import useIsMobile from 'src/Hooks/Shared/useIsMobile';

import styles from './RowActions.module.scss';
import { Props, RowAction } from './RowActions.types';

const RowActions = ({ actions, maxVisible = 3 }: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const modals = useModals();

  const visibleActions = actions.filter(action => !action.hidden);

  if (visibleActions.length === 0) return null;

  const runAction = (action: RowAction) => {
    if (action.confirm) {
      modals.openConfirmModal({
        title: action.confirm.title,
        children: <Text size="sm">{action.confirm.message}</Text>,
        labels: {
          confirm: action.confirm.confirmLabel ?? t('common.delete', 'Supprimer'),
          cancel: t('common.cancel', 'Annuler'),
        },
        confirmProps: { color: action.variant === 'danger' ? 'red' : undefined },
        onConfirm: action.onClick,
      });
    } else {
      action.onClick();
    }
  };

  const handleActionClick = (action: RowAction) => (e: MouseEvent) => {
    e.stopPropagation();
    if (action.disabled) return;
    runAction(action);
  };

  const stopEvent = (e: MouseEvent) => e.stopPropagation();

  if (isMobile) {
    return (
      <Menu shadow="md" width={220} position="bottom-end">
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={stopEvent}
            aria-label={t('common.more', 'Plus')}
          >
            <IconDotsVertical size={20} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {visibleActions.map(action => (
            <Menu.Item
              key={action.key}
              leftSection={action.icon}
              color={action.variant === 'danger' ? 'red' : undefined}
              disabled={action.disabled}
              onClick={handleActionClick(action)}
            >
              {action.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    );
  }

  const inline = visibleActions.slice(0, maxVisible);
  const overflow = visibleActions.slice(maxVisible);

  return (
    <div className={styles.container}>
      {inline.map(action => (
        <Tooltip
          key={action.key}
          label={action.label}
          withArrow
          openDelay={200}
          events={{ hover: true, focus: true, touch: true }}
        >
          <ActionIcon
            variant="subtle"
            size="lg"
            className={action.variant === 'danger' ? styles.danger : styles.default}
            data-disabled={action.disabled ? true : undefined}
            aria-disabled={action.disabled}
            aria-label={action.label}
            onClick={handleActionClick(action)}
          >
            {action.icon}
          </ActionIcon>
        </Tooltip>
      ))}
      {overflow.length > 0 && (
        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            {/* Icon-only trigger: keep an accessible name, there is no visible label. */}
            <ActionIcon
              variant="outline"
              size="lg"
              onClick={stopEvent}
              aria-label={t('common.more', 'Plus')}
            >
              <IconPlus size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {overflow.map(action => (
              <Menu.Item
                key={action.key}
                leftSection={action.icon}
                color={action.variant === 'danger' ? 'red' : undefined}
                disabled={action.disabled}
                onClick={handleActionClick(action)}
              >
                {action.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
    </div>
  );
};

export default RowActions;
